import { motion } from "framer-motion";
import { Zap, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { allProducts } from "@/data/productDatabase";
import { vehicles } from "@/data/boxCalculatorData";

const HeroSection = () => {
  const { user } = useAuth();

  // Live counts so the hero stats always match the current catalog and vehicle DB
  const productsStat = `${allProducts.length.toLocaleString()}+`;
  const vehiclesStat = `${vehicles.length.toLocaleString()}+`;
  // 6 box types (sealed, ported, 4th-order, 6th-order parallel/series, wall) per vehicle
  const designsStat = `${(vehicles.length * 6).toLocaleString()}+`;


  return (
    <section className="relative min-h-[85vh] sm:min-h-[90vh] flex items-center bg-hero-gradient overflow-hidden">
      {/* Blueprint grid overlay */}
      <div className="absolute inset-0 bg-blueprint-subtle opacity-40" />

      {/* Ambient glow orbs */}
      <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] rounded-full bg-primary/[0.06] blur-[150px] pointer-events-none" />
      <div className="absolute bottom-1/3 right-1/5 w-[500px] h-[500px] rounded-full bg-accent/[0.04] blur-[120px] pointer-events-none" />

      {/* Top fade */}
      <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-background/80 to-transparent pointer-events-none" />

      <div className="container relative z-10 py-10 sm:py-16 md:py-24 px-5 sm:px-6">
        <div className="max-w-3xl">
          {/* Badge — above headline */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-5 sm:mb-6 border border-primary/20 rounded-sm bg-primary/[0.06] backdrop-blur-sm">
              <Zap className="w-4 h-4 text-primary" />
              <span className="text-sm sm:text-base font-mono text-primary/80 uppercase tracking-wider">
                Car Audio Engineering Platform
              </span>
            </div>
          </motion.div>

          {/* Headline */}
          <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold font-display leading-[0.95] mb-4 sm:mb-5">
            Stop Guessing.{" "}
            <span className="text-gradient-primary">Start Getting Loud.</span>
          </h1>

          {/* Sub */}
          <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-xl mb-8 sm:mb-10 leading-relaxed">
            Precision tools for enclosure design, product comparison, and wiring — 
            backed by real data, not marketing fluff.
          </p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15, ease: [0.2, 0.8, 0.2, 1] }}
          >
            {/* Stats row */}
            <div className="flex flex-wrap gap-6 sm:gap-10 mb-8 sm:mb-10">
              {[
                { label: "Products", value: productsStat },
                { label: "Box Designs", value: designsStat },
                { label: "Vehicles", value: vehiclesStat },
              ].map((stat) => (
                <div key={stat.label} className="relative">
                  <div className="text-2xl sm:text-3xl font-mono font-bold text-foreground">{stat.value}</div>
                  <div className="text-sm sm:text-base font-mono text-muted-foreground uppercase tracking-wider">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap gap-4">
              <Link to={user ? "/box-calculator" : "/auth"}>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  className="group flex items-center gap-2 px-5 sm:px-7 py-3 sm:py-3.5 bg-primary text-primary-foreground font-semibold text-base rounded-sm shadow-hard hover:brightness-110 transition-all"
                >
                  {user ? "Start Building" : "Sign Up Free"}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </motion.button>
              </Link>
              <Link to={user ? "/products" : "/auth"}>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  className="px-5 sm:px-7 py-3 sm:py-3.5 border border-border text-foreground font-semibold text-base rounded-sm shadow-hard hover:bg-secondary hover:border-primary/30 transition-all"
                >
                  Compare Products
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent pointer-events-none" />
    </section>
  );
};

export default HeroSection;
